var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useEffect, useMemo, useState } from 'react';
import { customEvent, EventError, EventType } from '@assets/utils/customEvent';
import { KEY_FIRST_VIDEOS_RECEIVED, KEY_VIDEOS_STARTED, } from '@assets/utils/fwnPerformance/constants';
import { useGlobalStore } from '@core/hooks/useGlobalStore';
import { getHydratedVideosSelector } from '@core/hydration';
import { useApiWithParams } from './useApi';
const DEFAULT_PAGE = 10;
export var EmbedFeedType;
(function (EmbedFeedType) {
    EmbedFeedType["PUBLISHER_CLIENT"] = "publisher_client";
    EmbedFeedType["USER_TIMELINE"] = "user_timeline";
    EmbedFeedType["USER_TIMELINE_HASHTAG"] = "user_timeline_hashtag";
    EmbedFeedType["PLAYLIST"] = "playlist";
    EmbedFeedType["PLAYLIST_COLLECTION"] = "playlist_collection";
    EmbedFeedType["HASHTAG"] = "hashtag";
})(EmbedFeedType || (EmbedFeedType = {}));
const updateFeedItemsWithHydratedPartials = (videos, hydratedVideos) => {
    return videos.map((video) => {
        if (!(hydratedVideos === null || hydratedVideos === void 0 ? void 0 : hydratedVideos[video.encoded_id])) {
            return video;
        }
        else {
            return Object.assign(Object.assign({}, video), hydratedVideos[video.encoded_id]);
        }
    });
};
export const useEmbedFeed = ({ config, guestId, params, performance, }) => {
    const api = useApiWithParams(params);
    const hydratedVideos = useGlobalStore(getHydratedVideosSelector);
    /**
     * CONFIGURATION
     */
    const maxVideos = config['maxVideos'];
    const [state, setState] = useState({
        loading: true,
        feed: null,
        error: null,
    });
    const createFeedRequest = useMemo(() => {
        switch (config.type) {
            case EmbedFeedType.USER_TIMELINE:
                return (pageSize) => {
                    var _a;
                    return api.createEmbedUserTimelineFeed((_a = config.username) !== null && _a !== void 0 ? _a : '', pageSize, config.firstVideoId);
                };
            case EmbedFeedType.USER_TIMELINE_HASHTAG:
                return (pageSize) => {
                    var _a;
                    return api.createEmbedUserTimelineHashtagFeed((_a = config.username) !== null && _a !== void 0 ? _a : '', config.hashtagExpression, pageSize, config.firstVideoId);
                };
            case EmbedFeedType.PLAYLIST:
                return (pageSize) => {
                    var _a;
                    return api.createEmbedPlaylistFeed((_a = config.playlistId) !== null && _a !== void 0 ? _a : '', pageSize, config.firstVideoId);
                };
            case EmbedFeedType.PLAYLIST_COLLECTION:
                return () => api.createEmbedPlaylistCollectionFeed(params === null || params === void 0 ? void 0 : params.playlist_collection);
            case EmbedFeedType.HASHTAG:
                return (pageSize) => {
                    var _a;
                    return api.createEmbedHashtagFeed((_a = config.hashtag) !== null && _a !== void 0 ? _a : '', pageSize, config.firstVideoId);
                };
            case EmbedFeedType.PUBLISHER_CLIENT:
                return (pageSize) => {
                    var _a;
                    return api.createEmbedPublisherClientFeed(config.publisherClientId, guestId !== null && guestId !== void 0 ? guestId : '', (_a = params === null || params === void 0 ? void 0 : params.page_url) !== null && _a !== void 0 ? _a : '', pageSize, config.firstVideoId);
                };
            default:
                throw new Error('NOT IMPLEMENTED');
        }
    }, [config, state.feed]);
    /**
     * HOOK LOGIC
     */
    const createFeed = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const pageSize = Math.min(DEFAULT_PAGE, maxVideos || Infinity);
            const newFeed = yield createFeedRequest(pageSize);
            setState(Object.assign(Object.assign({}, state), { loading: false, feed: newFeed }));
        }
        catch (e) {
            params &&
                params.target &&
                customEvent({
                    data: { detail: EventError.API_ERROR },
                    target: params.target,
                    type: [EventType.FW_ERROR, EventType.FW_EMBEDFEED_ERROR],
                });
            setState({
                loading: false,
                feed: null,
                error: e,
            });
        }
    });
    useEffect(() => {
        ;
        (() => __awaiter(void 0, void 0, void 0, function* () {
            performance === null || performance === void 0 ? void 0 : performance.mark(KEY_VIDEOS_STARTED);
            yield createFeed();
            params &&
                customEvent({
                    data: {},
                    target: params.target,
                    type: EventType.FW_EMBEDFEED_CONTENTREADY,
                });
            performance === null || performance === void 0 ? void 0 : performance.mark(KEY_FIRST_VIDEOS_RECEIVED);
            performance === null || performance === void 0 ? void 0 : performance.measure(KEY_FIRST_VIDEOS_RECEIVED, KEY_VIDEOS_STARTED);
        }))();
    }, [
        config.type,
        config.maxVideos,
        config.firstVideoId,
        config.username,
        config.playlistId,
        config.publisherClientId,
        config.hashtag,
    ]);
    const loadMore = useCallback((itemsCount = DEFAULT_PAGE) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c;
        try {
            const { feed, loading } = state;
            if (!(feed === null || feed === void 0 ? void 0 : feed.next_page) ||
                loading ||
                (maxVideos && (feed === null || feed === void 0 ? void 0 : feed.feed_items) && feed.feed_items.length >= maxVideos)) {
                return;
            }
            const pageSize = Math.min(itemsCount, maxVideos
                ? maxVideos - ((feed === null || feed === void 0 ? void 0 : feed.feed_items) ? feed === null || feed === void 0 ? void 0 : feed.feed_items.length : 0)
                : Infinity);
            const feedWithNewItems = feed
                ? yield api.getMoreFeedItems(feed, pageSize)
                : null;
            setState(Object.assign(Object.assign({}, state), { feed: Object.assign(Object.assign(Object.assign({}, feed), feedWithNewItems), { feed_items: [
                        ...((_a = feed === null || feed === void 0 ? void 0 : feed.feed_items) !== null && _a !== void 0 ? _a : []),
                        ...((_b = feedWithNewItems === null || feedWithNewItems === void 0 ? void 0 : feedWithNewItems.feed_items) !== null && _b !== void 0 ? _b : []),
                    ] }) }));
        }
        catch (e) {
            if (((_c = e === null || e === void 0 ? void 0 : e.response) === null || _c === void 0 ? void 0 : _c.status) === 404) {
                yield createFeed();
            }
            else {
                setState({
                    loading: false,
                    feed: null,
                    error: e,
                });
            }
        }
    }), [state]);
    const { loading, feed, error } = state;
    const videos = useMemo(() => {
        if (!(feed === null || feed === void 0 ? void 0 : feed.feed_items)) {
            return [];
        }
        const videos = (feed === null || feed === void 0 ? void 0 : feed.feed_items.filter((item) => !!item.video).map((item) => {
            item.video.variant = item.variant;
            return item.video;
        })) || [];
        return updateFeedItemsWithHydratedPartials(videos, hydratedVideos);
    }, [feed === null || feed === void 0 ? void 0 : feed.feed_items, hydratedVideos]);
    const playlists = useMemo(() => {
        var _a;
        if ((_a = feed) === null || _a === void 0 ? void 0 : _a.playlists) {
            return feed.playlists;
        }
        return [];
    }, [feed]);
    const hasMore = useMemo(() => {
        if (!(feed === null || feed === void 0 ? void 0 : feed.next_page)) {
            return false;
        }
        if (maxVideos && maxVideos <= feed.feed_items.length) {
            return false;
        }
        return true;
    }, [feed, maxVideos]);
    return [
        { hasMore, error, videos, loading, id: feed === null || feed === void 0 ? void 0 : feed.id, playlists },
        { loadMore },
    ];
};
